/**
 * ChatPageContext — lets a dashboard publish its on-screen filters/period to the
 * global AI chat widget.
 *
 * The floating chat is mounted once at the app root, so it can only read what is
 * in the URL. Dashboards hold their active filters (province/district/disease,
 * period year/week/month) in component state that never reaches the URL, so the
 * chat answered generically for them. A page calls usePublishChatPageContext(...)
 * with its current filters; the widget reads them via useChatPageState() and
 * sends them in page_context, which the engine already understands
 * (_page_context_text reads disease/province/district/period/date_range/filters).
 */
import React, { createContext, useContext, useState, useMemo, useEffect } from 'react';

export interface ChatPageState {
  disease?: string;
  province?: string;
  district?: string;
  /** A human/engine-readable period, e.g. "2026", "2026-09", "2026-W37". */
  period?: string;
  /** An explicit range, e.g. "2026-01-01 to 2026-09-17". */
  date_range?: string;
  /** Friendly dashboard name, surfaced to the model as the page title. */
  dashboard?: string;
  /** Any other active filters worth sending, as a flat label->value map. */
  filters?: Record<string, string | number | undefined>;
  /**
   * A compact summary of the data the page is currently showing, built from
   * what the page already fetched (e.g. "Top diseases wk 37: Malaria 830,
   * Diarrhoea 210; 3 alerts active"). Keep it short -- the engine caps it at
   * ~600 chars. Lets the chat reason about what the user sees.
   */
  summary?: string;
}

interface ChatPageContextValue {
  pageState: ChatPageState | null;
  setPageState: (s: ChatPageState | null) => void;
}

const ChatPageContext = createContext<ChatPageContextValue>({
  pageState: null,
  setPageState: () => undefined,
});

export const ChatPageContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [pageState, setPageState] = useState<ChatPageState | null>(null);
  const value = useMemo(() => ({ pageState, setPageState }), [pageState]);
  return <ChatPageContext.Provider value={value}>{children}</ChatPageContext.Provider>;
};

/** Read the current page's published filters (used by the chat widget). */
export function useChatPageState(): ChatPageState | null {
  return useContext(ChatPageContext).pageState;
}

/**
 * Publish this page's filters/period to the chat widget. Clears automatically on
 * unmount so the widget never carries a stale page's filters onto another page.
 * Pass undefined/null (or an empty object) when nothing is selected.
 */
export function usePublishChatPageContext(state: ChatPageState | null | undefined): void {
  const { setPageState } = useContext(ChatPageContext);
  // Stable dependency so we only re-publish when the values actually change.
  const key = JSON.stringify(state || null);
  useEffect(() => {
    const hasAny = !!state && Object.values(state).some(
      (v) => v !== undefined && v !== null && v !== '' &&
             !(typeof v === 'object' && Object.keys(v).length === 0),
    );
    setPageState(hasAny ? state! : null);
    return () => setPageState(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}
